import React, { useCallback, useEffect, useState } from 'react';
import { CentroCostoNode } from './TreeNode';

interface CdCSelectorProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
}

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Flatten an albero into a list with depth info for indented display. */
function flattenTree(
  nodes: CentroCostoNode[],
  depth = 0,
): { id: string; label: string }[] {
  const result: { id: string; label: string }[] = [];
  for (const node of nodes) {
    const prefix = '─'.repeat(depth > 0 ? depth : 0);
    const label = depth > 0 ? `${prefix} ${node.codice} ${node.nome}` : `${node.codice} ${node.nome}`;
    result.push({ id: node.id, label });
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, depth + 1));
    }
  }
  return result;
}

const CdCSelector: React.FC<CdCSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Nessun centro',
}) => {
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/centri-costo/centri/albero', {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data: CentroCostoNode[] = await res.json();
      setOptions(flattenTree(data));
    } catch {
      // silently ignore — empty select
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  return (
    <select
      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      value={value ?? ''}
      disabled={loading}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.id} value={opt.id}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

export default CdCSelector;
